import { useInfiniteQuery, useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import {
  BulkCreateResponseDTO,
  CreditCardApiListCreditCardChargeNotificationsRequest,
  CreditCardApiListCreditCardsRequest,
  EmployeeCreditCardDTODeliveryModeEnum,
  FilterEmployeesDTO,
} from 'probonio-shared-ui/api';
import { apis, useTenantQuery } from 'probonio-shared-ui/module/api';
import { useWithMessage } from 'probonio-shared-ui/module/error';
import { useTenantID } from 'probonio-shared-ui/module/me';
import { EmployeeFilter, mapEmployeeFilterToRequest } from '../employeeStatusList/employeeFilterState';

export function mapRowSelectionToFilterRequest(
  rowSelectionState: { allSelected: boolean; rowSelection: Record<string, boolean> },
  employeeFilter?: EmployeeFilter,
  additonalFilter?: Partial<FilterEmployeesDTO>,
): {
  mappedEmployeeFilter?: FilterEmployeesDTO;
  employeeIds?: string[];
} {
  if (rowSelectionState.allSelected) {
    return {
      mappedEmployeeFilter: {
        ...mapEmployeeFilterToRequest(employeeFilter),
        ...additonalFilter,
      },
    };
  } else {
    const employeeIds = Object.keys(rowSelectionState.rowSelection).filter(id => rowSelectionState.rowSelection[id]);
    return { employeeIds };
  }
}

export function useOrderCreditCardsMutation(
  filters?: { employeeFilter?: FilterEmployeesDTO; employeeIds?: string[]; deliveryMode?: EmployeeCreditCardDTODeliveryModeEnum },
  options?: UseMutationOptions<BulkCreateResponseDTO, Error, void>,
) {
  const getTenantId = useTenantID();
  const withMessage = useWithMessage();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apis.creditCard
        .bulkCreateCreditCards({
          tenantId: getTenantId(),
          bulkCreateEmployeeCreditCardsDTO: {
            ...filters,
          },
        })
        .then(res => res.data),
    onError(error: Error) {
      withMessage(error);
    },
    ...options,
    async onSuccess(data, variables, onMutateResult, context) {
      await queryClient.invalidateQueries({ queryKey: ['tenants', getTenantId(), 'benefits', 'creditCard', 'cards'] });
      await queryClient.invalidateQueries({ queryKey: ['tenants', getTenantId(), 'employees'] });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}

const PAGE_SIZE = 15;

export function useInfiniteTenantCreditCards(
  parameters?: Omit<CreditCardApiListCreditCardsRequest, 'tenantId' | 'page' | 'pageSize'>,
  options?: { enabled?: boolean },
) {
  const getTenantId = useTenantID();

  return useInfiniteQuery({
    queryKey: ['tenants', getTenantId(), 'benefits', 'creditCard', 'cards', parameters, apis.creditCard.listCreditCards.name],
    queryFn: ({ pageParam = 0 }) =>
      apis.creditCard
        .listCreditCards({ tenantId: getTenantId(), page: pageParam, pageSize: PAGE_SIZE, ...parameters })
        .then(res => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => (pages.length * PAGE_SIZE < lastPage.totalCount ? pages.length : undefined),
    placeholderData: previousData => previousData,
    ...options,
  });
}

export function useTenantCreditCards(parameters?: Omit<CreditCardApiListCreditCardsRequest, 'tenantId'>, options?: { enabled?: boolean }) {
  return useTenantQuery(
    ['benefits', 'creditCard', 'cards', parameters, apis.creditCard.listCreditCards.name],
    tenantId => apis.creditCard.listCreditCards({ tenantId, ...parameters }).then(res => res.data),
    options,
  );
}

export function useTenantCreditCardChargeNotifications(
  parameters?: Omit<CreditCardApiListCreditCardChargeNotificationsRequest, 'tenantId'>,
  options?: { enabled?: boolean },
) {
  return useTenantQuery(
    ['benefits', 'creditCard', 'chargeNotifications', parameters, apis.creditCard.listCreditCardChargeNotifications.name],
    tenantId => apis.creditCard.listCreditCardChargeNotifications({ tenantId, ...parameters }).then(res => res.data),
    options,
  );
}

export function useInfiniteTenantCreditCardChargeNotifications(
  parameters?: Omit<CreditCardApiListCreditCardChargeNotificationsRequest, 'tenantId' | 'page' | 'pageSize'>,
  options?: { enabled?: boolean },
) {
  const getTenantId = useTenantID();

  return useInfiniteQuery({
    queryKey: [
      'tenants',
      getTenantId(),
      'benefits',
      'creditCard',
      'chargeNotifications',
      parameters,
      apis.creditCard.listCreditCardChargeNotifications.name,
    ],
    queryFn: ({ pageParam = 0 }) =>
      apis.creditCard
        .listCreditCardChargeNotifications({ tenantId: getTenantId(), page: pageParam, pageSize: PAGE_SIZE, ...parameters })
        .then(res => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages) => (pages.length * PAGE_SIZE < lastPage.totalCount ? pages.length : undefined),
    placeholderData: previousData => previousData,
    ...options,
  });
}
